/**
 * Valida e formata um CPF no formato atual.
 * @param cpf CPF a ser validado.
 * @param format Retorna o CPF formatado caso seja válido, se `true`.
 * @returns O CPF formatado se for válido e `format` for `true`, ou os mesmos dados de entrada se for inválido.
 */
export const validaCPF = (cpf: string, format: boolean = false): string => {
  if (!cpf)
    return cpf;

  // Remove caracteres não numéricos
  const cleaned = cpf.replace(/\D/g, '');

  // Verifica o comprimento do CPF (apenas 11 dígitos são válidos)
  if (cleaned.length !== 11) return cpf;

  // Verifica se todos os dígitos são iguais (ex.: "111.111.111-11")
  if (/^(\d)\1{10}$/.test(cleaned)) return cpf;

  // Calcula os dígitos verificadores
  const calculaDV = (base: string): string => {
    const calcularDigito = (base: string, pesoInicial: number): number => {
      const soma = base
        .split('')
        .map((num, i) => parseInt(num, 10) * (pesoInicial - i))
        .reduce((acc, curr) => acc + curr, 0);

      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const dv1 = calcularDigito(base, 10);
    const dv2 = calcularDigito(base + dv1, 11);

    return `${dv1}${dv2}`;
  };

  // Base do CPF e dígitos verificadores
  const base = cleaned.slice(0, 9);
  const dv = cleaned.slice(9);

  // Verifica se os dígitos verificadores são válidos
  const isValid = calculaDV(base) === dv;

  if (!isValid) return cpf;

  // Retorna o CPF formatado, se solicitado
  if (format) {
    return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }

  return cpf;
};

/**
 * Verifica se um CPF é válido.
 * @param cpf CPF a ser validado.
 * @returns true se o CPF for válido, false caso contrário.
 */
export const isValidCPF = (cpf: string): boolean => {
  if (!cpf)
    return false;

  const cleaned = cpf.replace(/\D/g, '');

  if (cleaned.length !== 11)
    return false;

  if (/^(\d)\1{10}$/.test(cleaned))
    return false;

  const calcularDigito = (base: string, pesoInicial: number): number => {
    const soma = base
      .split('')
      .map((num, i) => parseInt(num, 10) * (pesoInicial - i))
      .reduce((acc, curr) => acc + curr, 0);

    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const base = cleaned.slice(0, 9);
  const dv = cleaned.slice(9);

  const dv1 = calcularDigito(base, 10);
  const dv2 = calcularDigito(base + dv1, 11);

  return dv === `${dv1}${dv2}`;
};
